import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { Route, Link, withRouter } from 'react-router-dom';

import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui/Table';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import AutoComplete from 'material-ui/AutoComplete';
import Download from 'material-ui/svg-icons/file/file-download';

import classnames from 'classnames';

import sortParticipants from '../../utils/sortParticipants';
import FilenameWithIcon from '../../UI/FilenameWithIcon';
import FormTextField from '../../UI/FormTextField';
import FormAutoComplete from '../../UI/FormAutoComplete';

import MergeDialog from './MergeDialog';
import Paper from '../Paper';

import config from '../../config';
import styles from './ParticipantsTable.css';

const inlineStyles = {
    download: {
        width: '16px',
        height: '16px',
        position: 'relative',
        top: '4px',
        marginLeft: '4px',
    },
    paperSpan: {
        cursor: 'pointer',
    },
};

class ParticipantsTable extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        participants: PropTypes.array,
        editable: PropTypes.bool,
        season: PropTypes.string,
    };

    static defaultProps = {
        editable: false,
    };

    state = {
        participants: [],
        selectedRows: [],
        toMerge: null,
    };

    componentDidMount() {
        let participants = JSON.parse(JSON.stringify(this.props.participants));

        this.setState({ participants: sortParticipants(participants) });
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.participants !== nextProps.participants) {
            let participants = JSON.parse(JSON.stringify(nextProps.participants));

            this.setState({ participants: sortParticipants(participants) });
        }
    }

    handleRowSelection = selectedRows => {
        this.setState({ selectedRows });
    };

    handleDeleteClick = () => {
        if (this.state.selectedRows.length) {
            this.setState({
                participants: this.state.participants.filter(
                    (paricipant, index) => !this.state.selectedRows.includes(index)
                ),
                selectedRows: [],
            });
        }
    };

    handleMergeClick = () => {
        if (this.state.selectedRows.length) {
            const toMerge = this.state.participants.filter((paricipant, index) =>
                this.state.selectedRows.includes(index)
            );

            const names = Array.from(new Set(toMerge.filter(p => p.name).map(p => p.name)));
            const emails = Array.from(new Set(toMerge.filter(p => p.email).map(p => p.email)));
            const schools = Array.from(new Set(toMerge.filter(p => p.school).map(p => p.school)));
            const genders = Array.from(new Set(toMerge.filter(p => p.gender).map(p => p.gender)));
            const grades = Array.from(new Set(toMerge.filter(p => p.grade).map(p => p.grade)));

            if (
                names.length <= 1 &&
                emails.length <= 1 &&
                schools.length <= 1 &&
                genders.length <= 1 &&
                grades.length <= 1
            ) {
                const participantInfo = {
                    name: names[0] || '',
                    email: emails[0] || '',
                    school: schools[0] || '',
                    grade: grades[0] || '',
                    genders: genders[0] || '',
                };

                this.handleMergeSave(participantInfo, toMerge);
            } else {
                this.setState({
                    toMerge,
                });
            }
        }
    };

    handleMergeCancel = () => {
        this.setState({
            toMerge: null,
        });
    };

    handleMergeSave = (participantInfo, toMerge = this.state.toMerge) => {
        let participant = {
            ...participantInfo,
            paperDrafts: toMerge.map(p => p.paperDrafts).reduce((res, item) => res.concat(item), []),
        };

        let participants = this.state.participants.filter(
            (paricipant, index) => !this.state.selectedRows.includes(index)
        );

        participants.push(participant);

        this.setState({
            participants: sortParticipants(participants),
            toMerge: null,
            selectedRows: [],
        });
    };

    handleSaveClick = () => {
        this.setState({ saving: true });

        this.props.onSave(this.state.participants);
    };

    handlePaperClick = e => {
        e.stopPropagation();

        this.paperOpenedByUser = true;
    };

    handlePaperCloseClick = () => {
        const {
            onPaperClose,
            history,
            match: { url },
        } = this.props;

        onPaperClose && onPaperClose();

        if (this.paperOpenedByUser) {
            history.goBack();
        } else {
            history.replace(url);
        }
    };

    stopPropagation = event => event.stopPropagation();

    handleUpdateParticipant = (fieldName, value, numberOnly) => {
        let [index, field] = fieldName.split('_');

        index = parseInt(index, 10);

        if (numberOnly) {
            value = value.replace(/\D/g, '');

            if (value !== '') {
                value = parseInt(value, 10);
            }
        }

        this.setState({
            participants: [
                ...this.state.participants.slice(0, index),
                {
                    ...this.state.participants[index],
                    [field]: value,
                    errors: {
                        ...this.state.participants[index].errors,
                        [field]: '',
                    },
                },
                ...this.state.participants.slice(index + 1),
            ],
        });
    };

    renderEditableParticipant = (participant, i) => {
        const {
            intl: { formatMessage },
            match: { url },
        } = this.props;
        const { name, school, grade, email, gender, papers, paperDrafts } = participant;

        return (
            <TableRow selectable key={i} selected={this.state.selectedRows.includes(i)} displayBorder={false}>
                <TableRowColumn key="name" className={styles.name}>
                    <FormTextField
                        value={name || ''}
                        name={`${i}_name`}
                        onChange={this.handleUpdateParticipant}
                        className={styles.name}
                        errorText={''}
                        errorStyle={{ float: 'left' }}
                        onClick={this.stopPropagation}
                        fullWidth
                    />
                </TableRowColumn>
                <TableRowColumn key="gender" className={styles.gender}>
                    <FormAutoComplete
                        searchText={gender || ''}
                        dataSource={this.props.genders}
                        filter={AutoComplete.noFilter}
                        name={`${i}_gender`}
                        onChange={this.handleUpdateParticipant}
                        style={{ width: 'auto' }}
                        openOnFocus
                        errorText={''}
                        errorStyle={{ float: 'left' }}
                        onClick={this.stopPropagation}
                        fullWidth
                    />
                </TableRowColumn>
                <TableRowColumn key="grade" className={styles.grade}>
                    <FormTextField
                        value={grade || ''}
                        name={`${i}_grade`}
                        onChange={this.handleUpdateParticipant}
                        errorText={''}
                        errorStyle={{ float: 'left' }}
                        onClick={this.stopPropagation}
                        fullWidth
                        numberOnly
                    />
                </TableRowColumn>
                <TableRowColumn key="school" className={styles.school}>
                    <FormTextField
                        value={school || ''}
                        name={`${i}_school`}
                        onChange={this.handleUpdateParticipant}
                        errorText={''}
                        errorStyle={{ float: 'left' }}
                        onClick={this.stopPropagation}
                        fullWidth
                    />
                </TableRowColumn>
                <TableRowColumn key="email" className={styles.email}>
                    <FormTextField
                        value={email || ''}
                        name={`${i}_email`}
                        onChange={this.handleUpdateParticipant}
                        errorText={''}
                        errorStyle={{ float: 'left' }}
                        onClick={this.stopPropagation}
                        fullWidth
                    />
                </TableRowColumn>
                <TableRowColumn key="papers" className={styles.papers}>
                    {(paperDrafts || papers).map((paper, j) => (
                        <div
                            className={classnames(styles.paperInfo, {
                                [styles.progress]: paper.juryMarks && paper.juryMarks.length,
                                [styles.ok]: paper.marks && paper.marks.length,
                            })}
                            key={paper._id}
                        >
                            <Link to={`${url}/papers/${paper._id}`} onClick={this.handlePaperClick}>
                                <FilenameWithIcon
                                    text={
                                        formatMessage({ id: 'ParticipantList.round.' + paper.round }) +
                                        (paper.pages && paper.pages.length ? ` (${paper.pages.length})` : null)
                                    }
                                    filename={paper.file}
                                    data-paper={paper._id}
                                    style={inlineStyles.paperSpan}
                                />
                            </Link>
                            <a
                                href={`${config.backend}/uploads/${paper.file}`}
                                download={paper.filename}
                                onClick={this.stopPropagation}
                                target="_blank"
                            >
                                <Download style={inlineStyles.download} />
                            </a>
                        </div>
                    ))}
                </TableRowColumn>
            </TableRow>
        );
    };

    renderParticipant = (participant, i) => {
        const {
            intl: { formatMessage },
            editable,
            match: { url },
            season,
            batchMode,
        } = this.props;

        let papersStyle = {};

        if (batchMode) {
            let paper = participant.papers[0];

            if (paper.marks && paper.marks.length) {
                papersStyle.background = 'linear-gradient(90deg, #b8e6b8 50%, #b8e6b8 50%)';
            } else if (paper.juryMarks && paper.juryMarks.some(mark => mark.stage === 'second')) {
                papersStyle.background = 'linear-gradient(90deg, #f2ef78 66%, transparent 66%)';
            } else if (paper.juryMarks && paper.juryMarks.some(mark => mark.stage === 'first')) {
                papersStyle.background = 'linear-gradient(90deg, #f2ef78 33%, transparent 33%)';
            }
        }

        return (
            <TableRow selectable={editable} key={i}>
                {!editable && (
                    <TableRowColumn key="number" className={styles.number}>
                        {i + 1}
                    </TableRowColumn>
                )}
                <TableRowColumn key="name" className={styles.name}>
                    {participant.name}
                </TableRowColumn>
                <TableRowColumn key="grade" className={styles.grade}>
                    {participant.grade}
                </TableRowColumn>
                <TableRowColumn key="school" className={styles.school}>
                    {participant.school}
                </TableRowColumn>
                {!batchMode && (
                    <TableRowColumn key="email" className={styles.email}>
                        {participant.email}
                    </TableRowColumn>
                )}
                <TableRowColumn key="papers" className={styles.papers} style={papersStyle}>
                    {(participant.paperDrafts || participant.papers)
                        .filter(
                            ({ round }) => (season === 'fall' ? ['p', 'm'].includes(round) : ['t', 'v'].includes(round))
                        )
                        .map((paper, j) => {
                            let style = {};

                            if (!batchMode) {
                                if (paper.marks && paper.marks.length) {
                                    style.background = 'linear-gradient(90deg, #b8e6b8 50%, #b8e6b8 50%)';
                                } else if (paper.juryMarks && paper.juryMarks.some(mark => mark.stage === 'second')) {
                                    style.background = 'linear-gradient(90deg, #f2ef78 66%, transparent 66%)';
                                } else if (paper.juryMarks && paper.juryMarks.some(mark => mark.stage === 'first')) {
                                    style.background = 'linear-gradient(90deg, #f2ef78 33%, transparent 33%)';
                                }
                            }

                            return (
                                <div className={styles.paperInfo} key={paper._id} style={style}>
                                    {paper.file && (
                                        <Link to={`${url}/papers/${paper._id}`} onClick={this.handlePaperClick}>
                                            <FilenameWithIcon
                                                text={
                                                    batchMode
                                                        ? paper.filename
                                                        : formatMessage({
                                                              id: 'ParticipantList.round.' + paper.round,
                                                          }) +
                                                          (paper.pages && paper.pages.length
                                                              ? ` (${paper.pages.length})`
                                                              : '')
                                                }
                                                filename={paper.file}
                                                data-paper={paper._id}
                                                style={inlineStyles.paperSpan}
                                            />
                                        </Link>
                                    )}
                                    {paper.file && (
                                        <a
                                            href={`${config.backend}/uploads/${paper.file}`}
                                            download={paper.filename}
                                            onClick={this.stopPropagation}
                                            target="_blank"
                                        >
                                            <Download style={inlineStyles.download} />
                                        </a>
                                    )}
                                    {!paper.file && <FormattedMessage id={'ParticipantList.round.' + paper.round} />}
                                </div>
                            );
                        })}
                </TableRowColumn>
            </TableRow>
        );
    };

    render() {
        const {
            intl: { formatMessage },
            editable,
            fullEdit,
            tournament,
            match: { path },
            batchMode,
        } = this.props;
        const { toMerge, selectedRows, saving, participants } = this.state;

        return (
            <div>
                {!!selectedRows.length && (
                    <div className={styles.buttons}>
                        <FlatButton
                            label={formatMessage({ id: 'ParticipantList.delete' })}
                            onClick={this.handleDeleteClick}
                            primary
                        />
                        <FlatButton
                            label={formatMessage({ id: 'ParticipantList.merge' })}
                            onClick={this.handleMergeClick}
                            primary
                            disabled={selectedRows.length < 2}
                        />
                    </div>
                )}
                <Table
                    multiSelectable={editable}
                    onRowSelection={this.handleRowSelection}
                    className={classnames({
                        [styles.fullEdit]: fullEdit,
                        [styles.batchMode]: batchMode,
                    })}
                >
                    <TableHeader
                        displaySelectAll={false}
                        adjustForCheckbox={editable}
                        enableSelectAll={false}
                        selectable={editable}
                    >
                        <TableRow>
                            {!editable && <TableHeaderColumn key="number" className={styles.number} />}
                            <TableHeaderColumn key="name" className={styles.name}>
                                <FormattedMessage id="ParticipantList.name" />
                            </TableHeaderColumn>
                            {fullEdit && (
                                <TableHeaderColumn key="gender" className={styles.gender}>
                                    <FormattedMessage id="ParticipantList.gender" />
                                </TableHeaderColumn>
                            )}
                            <TableHeaderColumn key="grade" className={styles.grade}>
                                <FormattedMessage id="ParticipantList.grade" />
                            </TableHeaderColumn>
                            <TableHeaderColumn key="school" className={styles.school}>
                                <FormattedMessage id="ParticipantList.school" />
                            </TableHeaderColumn>
                            {!batchMode && (
                                <TableHeaderColumn key="email" className={styles.email}>
                                    <FormattedMessage id="ParticipantList.email" />
                                </TableHeaderColumn>
                            )}
                            <TableHeaderColumn key="papers" className={styles.papers}>
                                <FormattedMessage id="ParticipantList.papers" />
                            </TableHeaderColumn>
                        </TableRow>
                    </TableHeader>
                    <TableBody displayRowCheckbox={editable} deselectOnClickaway={false}>
                        {participants.map(fullEdit ? this.renderEditableParticipant : this.renderParticipant)}
                    </TableBody>
                </Table>
                <MergeDialog
                    open={!!toMerge}
                    participants={toMerge}
                    onCancel={this.handleMergeCancel}
                    onSave={this.handleMergeSave}
                />
                {editable && (
                    <RaisedButton
                        primary
                        label={formatMessage({ id: 'RegList.save' })}
                        onClick={this.handleSaveClick}
                        className={styles.button}
                        disabled={saving}
                    />
                )}
                {!!participants.length && (
                    <Route
                        path={`${path}/papers/:paperId`}
                        render={({
                            match: {
                                params: { paperId },
                            },
                        }) => {
                            const participant = participants.find(p =>
                                (p.paperDrafts || p.papers).find(p => p._id === paperId)
                            );
                            const selectedPaper = (participant.paperDrafts || participant.papers).find(
                                p => p._id === paperId
                            );

                            return (
                                <Paper
                                    {...selectedPaper}
                                    onCloseClick={this.handlePaperCloseClick}
                                    showMarks={!!selectedPaper.marks}
                                    editable={!!selectedPaper.marks}
                                    tournament={tournament}
                                />
                            );
                        }}
                    />
                )}
            </div>
        );
    }
}

export default withRouter(injectIntl(ParticipantsTable));



// WEBPACK FOOTER //
// ./src/Components/ParticipantsTable/ParticipantsTable.js